import React, { useState, useMemo, useCallback } from "react";
import { useTrialsData } from "./hooks/useTrialsData";
import TrialsStats from "./components/TrialsStats";
import TrialsTable from "./components/TrialsTable";
import { 
    Breadcrumb, 
    NeoCard, 
    NeoButton, 
    NeoInput, 
    NeoEmptyState,
    NeoPills,
    NeoDateRangePicker
} from "../../../components/Common";
import { toISODate, addDays, getStartOfWeek } from "../../../utils/date";

/**
 * TrialsPage (Aulas Experimentais)
 * Tela de gestão e comparecimento de aulas experimentais do NeoApp.
 * Design reestruturado para ser leve, espaçoso, integrado e 100% fiel ao Neo Design System.
 */
const TrialsPage = () => {
    document.title = "Aulas Experimentais | NeoApp - Sistema de Gestão";

    // ─── Período ───
    const today = new Date();
    const [preset, setPreset] = useState("TODAY");
    
    const [startDate, setStartDate] = useState(() => toISODate(today));
    const [endDate, setEndDate] = useState(() => toISODate(today));

    // ─── Filtros client-side ───
    const [filterEmployee, setFilterEmployee] = useState("");
    const [filterConsultant, setFilterConsultant] = useState("");

    const { trials, loading, error, hasFetched, fetchTrials } = useTrialsData();

    // ─── Buscar ao clicar no botão ───
    const handleSearch = useCallback(() => {
        fetchTrials(startDate, endDate);
    }, [fetchTrials, startDate, endDate]);

    // ─── Extrair listas únicas para os selects ───
    const employees = useMemo(() => {
        const names = [...new Set(trials.map(t => t.employeeName).filter(Boolean))];
        return names.sort();
    }, [trials]);

    const consultants = useMemo(() => {
        const names = [...new Set(trials.map(t => t.consultantName).filter(Boolean))];
        return names.sort();
    }, [trials]);

    // ─── Filtrar trials ───
    const filteredTrials = useMemo(() => {
        let result = trials;
        if (filterEmployee) {
            result = result.filter(t => t.employeeName === filterEmployee);
        }
        if (filterConsultant) {
            result = result.filter(t => t.consultantName === filterConsultant);
        }
        return result;
    }, [trials, filterEmployee, filterConsultant]);

    // ─── Presets de período ───
    const handlePresetChange = (newPreset) => {
        setPreset(newPreset);
        const now = new Date();
        if (newPreset === "TODAY") {
            setStartDate(toISODate(now));
            setEndDate(toISODate(now));
        } else if (newPreset === "WEEK") {
            const startW = getStartOfWeek(now);
            const endW = addDays(startW, 6);
            setStartDate(toISODate(startW));
            setEndDate(toISODate(endW));
        } else if (newPreset === "MONTH") {
            const y = now.getFullYear();
            const m = now.getMonth();
            setStartDate(toISODate(new Date(y, m, 1)));
            setEndDate(toISODate(new Date(y, m + 1, 0)));
        }
    };


    // Opções para o seletor NeoPills
    const periodOptions = [
        { id: "TODAY", label: "Hoje" },
        { id: "WEEK", label: "Semana" },
        { id: "MONTH", label: "Mês" }
    ];

    return (
        <React.Fragment>
            {/* TOP BAR: Cabeçalho & Filtro de Período Lado a Lado (Super Limpo e Sem Card) */}
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between mb-4 gap-3">
                <div>
                    <Breadcrumb title="Aulas Experimentais" />
                    <p className="text-muted mb-0 font-size-13 mt-0.5">
                        Gestão de agendamentos, comparecimentos e taxas de conversão de leads.
                    </p>
                </div>

                {/* Filtro de Período Integrado Transparente */}
                <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2 flex-shrink-0">
                    {/* Seletor Segmentado Reutilizável NeoPills */}
                    <NeoPills
                        options={periodOptions}
                        activeId={preset}
                        onChange={handlePresetChange}
                    />

                    {/* Seletor Único de Período via NeoDateRangePicker */}
                    <div className="d-flex align-items-center gap-2">
                        <NeoDateRangePicker
                            startDate={startDate}
                            endDate={endDate}
                            onChange={({ startDate: s, endDate: e }) => {
                                if (s !== undefined) setStartDate(s);
                                if (e !== undefined) {
                                    setEndDate(e);
                                    if (e) setPreset("CUSTOM");
                                }
                            }}
                            size="sm"
                            className="mb-0 neo-daterangepicker-filter"
                            placeholder="Selecione o período..."
                        />
                        <NeoButton
                            variant="primary"
                            onClick={handleSearch}
                            loading={loading}
                            icon="mdi-magnify"
                            size="sm"
                            className="neo-filter-search-btn px-3 ms-1"
                        >
                            Buscar
                        </NeoButton>
                    </div>
                </div>
            </div>

            <div className="border-top mb-4"></div>

            <div className="trials-container">
                {/* Conteúdo dinâmico */}
                {hasFetched ? (
                    <>
                        {/* Indicadores de Métricas (Bespoke Summary Bar) */}
                        <TrialsStats trials={filteredTrials} />

                        {/* Tabela Principal Contida em um Único Card */}
                        <NeoCard
                            title="Agenda de Experimentais"
                            subtitle="Relação detalhada de agendamentos e comparecimentos no período filtrado."
                            headerIcon="mdi-calendar-check"
                        >
                            {/* Tabela de Dados */}
                            {error ? (
                                <div className="alert alert-danger mb-0" role="alert">
                                    {error}
                                    <br /><br />
                                    <small>Consulte o console do navegador para mais informações.</small>
                                </div>
                            ) : (
                                <TrialsTable
                                    trials={filteredTrials}
                                    loading={loading}
                                    filterComponent={
                                        <div className="d-flex gap-2 align-items-center">
                                            {/* Professor Select */}
                                            <div className="neo-filter-select-sm">
                                                <NeoInput
                                                    type="select"
                                                    value={filterEmployee}
                                                    onChange={(e) => setFilterEmployee(e.target.value)}
                                                    options={[
                                                        { value: "", label: "Todos os professores" },
                                                        ...employees.map(name => ({ value: name, label: name }))
                                                    ]}
                                                    size="sm"
                                                    className="mb-0"
                                                />
                                            </div>

                                            {/* Consultor Select */}
                                            <div className="neo-filter-select-sm">
                                                <NeoInput
                                                    type="select"
                                                    value={filterConsultant}
                                                    onChange={(e) => setFilterConsultant(e.target.value)}
                                                    options={[
                                                        { value: "", label: "Todos os consultores" },
                                                        ...consultants.map(name => ({ value: name, label: name }))
                                                    ]}
                                                    size="sm"
                                                    className="mb-0"
                                                />
                                            </div>

                                            {/* Botão de Limpeza */}
                                            {(filterEmployee || filterConsultant) && (
                                                <NeoButton
                                                    variant="outline-chocolate"
                                                    size="sm"
                                                    icon="mdi-filter-remove-outline"
                                                    onClick={() => { setFilterEmployee(""); setFilterConsultant(""); }}
                                                    title="Limpar filtros secundários"
                                                />
                                            )}
                                        </div>
                                    }
                                />
                            )}
                        </NeoCard>
                    </>
                ) : (
                    /* Estado Inicial Vazio */
                    <NeoEmptyState
                        icon="mdi-calendar-search"
                        title="Selecione o período e clique em Buscar"
                        description="Defina as datas desejadas na barra superior e clique em Buscar para exibir os agendamentos."
                    />
                )}
            </div>
        </React.Fragment>
    );
};

export default TrialsPage;
